import { Group } from '@app/shared/models/group';
import { Team } from '@app/shared/models/team';
import { User } from '@app/shared/models/user';
import { Action, createReducer, on } from '@ngrx/store';
import { TeamsActions } from '../actions';

export const teamsFeatureKey = 'teams';

export interface State {
  accountTeams: Team[];
  accountTeamsLoading: boolean;
  accountTeamsLoaded: boolean;
  accountTeamsLoadingError: any;

  guestDeleting: boolean;
  guestDeletingError: any;
  guestDeleted: boolean;

  teamDetails: Team;
  teamDetailsLoaded: boolean;
  teamDetailsLoading: boolean;
  teamDetailsLoadingError: any;

  teamGroups: Group[];
  teamGroupsLoaded: boolean;
  teamGroupsLoading: boolean;
  teamGroupsLoadingError: any;

  teamMembers: User[];
  teamMembersLoaded: boolean;
  teamMembersLoading: boolean;
  teamMembersLoadingError: any;

  teamCreating: boolean;
  teamCreated: boolean;
  teamCreatingError: any;

  teamUpdating: boolean;
  teamUpdated: boolean;
  teamUpdatingError: any;

  memberCreated: User;
  memberCreationPending: boolean;
  memberCreationError: any;

  detachTeamProcessing: boolean;
  detachTeamSuccess: boolean;
  detachTeamError: any;

  deleteTeamPending: boolean;
  deleteTeamSuccess: boolean;
  deleteTeamError: any;
  deleteTeamErrorData: any;
}

export const initialState: State = {
  accountTeams: null,
  accountTeamsLoading: false,
  accountTeamsLoaded: false,
  accountTeamsLoadingError: null,


  guestDeleting: false,
  guestDeletingError: null,
  guestDeleted: false,

  teamDetails: null,
  teamDetailsLoaded: false,
  teamDetailsLoading: false,
  teamDetailsLoadingError: null,

  teamGroups: [],
  teamGroupsLoaded: false,
  teamGroupsLoading: false,
  teamGroupsLoadingError: null,

  teamMembers: [],
  teamMembersLoaded: false,
  teamMembersLoading: false,
  teamMembersLoadingError: null,

  teamCreating: false,
  teamCreated: false,
  teamCreatingError: null,

  teamUpdating: false,
  teamUpdated: false,
  teamUpdatingError: null,

  memberCreated: null,
  memberCreationPending: false,
  memberCreationError: null,

  detachTeamProcessing: false,
  detachTeamSuccess: false,
  detachTeamError: null,

  deleteTeamPending: false,
  deleteTeamSuccess: false,
  deleteTeamError: null,
  deleteTeamErrorData: null,

};

const teamsReducer = createReducer(
  initialState,
  // Load Account Teams
  on(TeamsActions.loadAccountTeams, (state) => ({
    ...state,
    accountTeams: initialState.accountTeams,
    accountTeamsLoading: true,
    accountTeamsLoaded: initialState.accountTeamsLoaded,
    accountTeamsLoadingError: initialState.accountTeamsLoadingError,
  })),
  on(TeamsActions.loadAccountTeamsSuccess, (state, { teams }) => ({
    ...state,
    accountTeams: teams,
    accountTeamsLoading: initialState.accountTeamsLoading,
    accountTeamsLoaded: true,
  })),
  on(TeamsActions.loadAccountTeamsFailure, (state, { error }) => ({
    ...state,
    accountTeamsLoading: initialState.accountTeamsLoading,
    accountTeamsLoadingError: error,
  })),
  // Load Team Details
  on(TeamsActions.LoadTeamDetails, (state) => ({
    ...state,
    teamDetails: initialState.teamDetails,
    teamDetailsLoading: true,
    teamDetailsLoaded: initialState.teamDetailsLoaded,
    teamDetailsLoadingError: initialState.teamDetailsLoadingError,
  })),
  on(TeamsActions.LoadTeamDetailsSuccess, (state, { team }) => ({
    ...state,
    teamDetails: team,
    teamDetailsLoading: initialState.teamDetailsLoading,
    teamDetailsLoaded: true,
  })),
  on(TeamsActions.LoadTeamDetailsFailure, (state, { error }) => ({
    ...state,
    teamDetailsLoading: initialState.teamDetailsLoading,
    teamDetailsLoaded: initialState.teamDetailsLoaded,
    teamDetailsLoadingError: error,
  })),
  // Load Team Groups
  on(TeamsActions.LoadTeamGroups, (state) => ({
    ...state,
    teamGroups: initialState.teamGroups,
    teamGroupsLoading: true,
    teamGroupsLoaded: initialState.teamGroupsLoaded,
    teamGroupsLoadingError: initialState.teamGroupsLoadingError,
  })),
  on(TeamsActions.LoadTeamGroupsSuccess, (state, { groups }) => ({
    ...state,
    teamGroups: groups,
    teamGroupsLoading: initialState.teamGroupsLoading,
    teamGroupsLoaded: true,
  })),
  on(TeamsActions.LoadTeamGroupsFailure, (state, { error }) => ({
    ...state,
    teamGroupsLoading: initialState.teamGroupsLoading,
    teamGroupsLoaded: initialState.teamGroupsLoaded,
    teamGroupsLoadingError: error,
  })),
  // Load Team Members
  on(TeamsActions.LoadTeamMembers, (state) => ({
    ...state,
    teamMembers: initialState.teamMembers,
    teamMembersLoading: true,
    teamMembersLoaded: initialState.teamMembersLoaded,
    teamMembersLoadingError: initialState.teamMembersLoadingError,
  })),
  on(TeamsActions.LoadTeamMembersSuccess, (state, { members }) => ({
    ...state,
    teamMembers: members,
    teamMembersLoading: initialState.teamMembersLoading,
    teamMembersLoaded: true,
  })),
  on(TeamsActions.LoadTeamMembersFailure, (state, { error }) => ({
    ...state,
    teamMembersLoading: initialState.teamMembersLoading,
    teamMembersLoaded: initialState.teamMembersLoaded,
    teamMembersLoadingError: error,
  })),
  // Create Team
  on(TeamsActions.addTeam, (state) => ({
    ...state,
    teamCreating: true,
    teamCreated: initialState.teamCreated,
    teamCreatingError: initialState.teamCreatingError,
  })),
  on(TeamsActions.addTeamSuccess, (state, { team }) => ({
    ...state,
    teamCreated: true,
    teamCreating: false,
  })),
  on(TeamsActions.addTeamFailure, (state, { error }) => ({
    ...state,
    teamCreating: initialState.teamCreating,
    teamCreatingError: error
  })),
  // Update Team
  on(TeamsActions.updateTeam, (state) => ({
    ...state,
    teamUpdating: true,
    teamUpdated: initialState.teamUpdated,
    teamUpdatingError: initialState.teamUpdatingError,
  })),
  on(TeamsActions.updateTeamSuccess, (state, { team }) => ({
    ...state,
    teamUpdating: initialState.teamUpdating,
    teamUpdated: true,
  })),
  on(TeamsActions.updateTeamFailure, (state, { error }) => ({
    ...state,
    teamUpdating: initialState.teamUpdating,
    teamUpdatingError: error
  })),
  on(TeamsActions.resetTeamCreationError, (state) => ({
    ...state,
    teamCreatingError: initialState.teamCreatingError,
  })),
  on(TeamsActions.AddEmail, (state) => ({
    ...state,
    teamUpdating: true,
    teamUpdatingError: initialState.teamUpdatingError,
    teamUpdated: initialState.teamUpdated,
  })),
  on(TeamsActions.AddEmailFailure, (state, { error }) => ({
    ...state,
    teamUpdating: false,
    teamUpdatingError: error,
  })),
  // Create member
  on(TeamsActions.addMember, (state) => ({
    ...state,
    memberCreated: initialState.memberCreated,
    memberCreationError: initialState.memberCreationError,
    memberCreationPending: true
  })),
  on(TeamsActions.addMemberSuccess, (state, { member }) => ({
    ...state,
    memberCreated: member,
    memberCreationPending: false
  })),
  on(TeamsActions.addMemberFailure, (state, { error }) => ({
    ...state,
    memberCreationError: error,
    memberCreationPending: false
  })),
  // Add Guest Pattern
  on(TeamsActions.AddEmailPattern, (state) => ({
    ...state,
    teamUpdating: true,
    teamUpdatingError: initialState.teamUpdatingError,
    teamUpdated: initialState.teamUpdated,
  })),
  on(TeamsActions.AddEmailPatternFailure, (state, { error }) => ({
    ...state,
    teamUpdating: false,
    teamUpdatingError: error,
  })),
  // Detach Guest From Team
  on(TeamsActions.DetachGuestFromTeam, (state) => ({
    ...state,
    guestDeleting: true,
    guestDeleted: initialState.guestDeleted,
  })),
  on(TeamsActions.DetachGuestFromTeamSuccess, (state) => ({
    ...state,
    guestDeleting: initialState.guestDeleting,
    guestDeleted: true,
  })),
  on(TeamsActions.DetachGuestFromTeamFailure, (state) => ({
    ...state,
    guestDeleting: initialState.guestDeleting,
    guestDeletingError: initialState.guestDeletingError,
    guestDeleted: true,
  })),
  // Update Team Config
  on(TeamsActions.UpdateTeamConfigSuccess, (state) => ({
    ...state,
    guestDeleting: initialState.guestDeleting,
    teamUpdated: true,
    teamUpdating: initialState.teamUpdating,
  })),
  // Attach member to team
  on(TeamsActions.DetachMemberFromTeam, (state) => ({
    ...state,
    detachTeamProcessing: true,
    detachTeamSuccess: initialState.detachTeamSuccess,
    detachTeamError: initialState.detachTeamError,
  })),
  on(TeamsActions.DetachMemberFromTeamSuccess, (state) => ({
    ...state,
    detachTeamProcessing: initialState.detachTeamProcessing,
    detachTeamSuccess: true,
  })),
  on(TeamsActions.DetachMemberFromTeamFailure, (state, { error }) => ({
    ...state,
    detachTeamProcessing: initialState.detachTeamProcessing,
    detachTeamError: error,
  })),
  // Reset ConfigUpdateError
  on(TeamsActions.ResetError, (state) => ({
    ...state,
    teamUpdatingError: initialState.teamUpdatingError,
  })),
  // Delete Team
  on(TeamsActions.DeleteTeam, (state) => ({
    ...state,
    deleteTeamPending: true,
    deleteTeamSuccess: initialState.deleteTeamSuccess,
    deleteTeamError: initialState.deleteTeamError
  })),
  on(TeamsActions.DeleteTeamSuccess, (state) => ({
    ...state,
    deleteTeamPending: false,
    deleteTeamSuccess: true,
    deleteTeamError: null
  })),
  on(TeamsActions.DeleteTeamFailure, (state, { error }) => ({
    ...state,
    deleteTeamPending: false,
    deleteTeamError: error,
  })),
  // Delete Team
  on(TeamsActions.ResetDeleteTeam, (state) => ({
    ...state,
    deleteTeamPending: initialState.deleteTeamPending,
    deleteTeamSuccess: initialState.deleteTeamSuccess,
    deleteTeamError: initialState.detachTeamError,
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return teamsReducer(state, action);
}
